import { useCallback, useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Box from '@/components/ui/Box/Box';
import Typography from '@/components/ui/Typography/Typography';
import Page from '@/components/business/Page/Page';
import { catalogApi, type CatalogEntry } from '@/api/catalog';
import {
    pricebookApi,
    type Pricebook,
    type PricebookRule,
    type PricebookSearchParams,
} from '@/api/pricebook';
import { extractRules } from '@/helpers/pricebook';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import SearchRules from './components/SearchRules/SearchRules';
import RulesTable from './components/RulesTable/RulesTable';
import { styles } from './styles';

const DEFAULT_PRICEBOOK_NAME = 'Default Price book';

const PricebooksPage = () => {
    const theme = useTheme();
    const companyCtx = useCompany();
    const company = companyCtx.state.company;
    const isRepairer = company?.marketplace_side === 'Repairer';

    const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
    const [vehicleMakes, setVehicleMakes] = useState<string[]>([]);
    const [pricebook, setPricebook] = useState<Pricebook | null>(null);
    const [rules, setRules] = useState<PricebookRule[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastParams, setLastParams] = useState<PricebookSearchParams | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async (): Promise<void> => {
            try {
                const [pricebooksRes, catalogRes, makesRes] = await Promise.all([
                    pricebookApi.listPricebooks(),
                    catalogApi.list(),
                    pricebookApi.getVehicleMakes(),
                ]);
                if (cancelled) return;
                const list = pricebooksRes.data?.payload ?? [];
                const def =
                    list.find((p) => p.name === DEFAULT_PRICEBOOK_NAME) ?? list[0] ?? null;
                if (def && !def.company_name) def.company_name = company?.name;
                setPricebook(def);
                setCatalog(catalogRes.data?.payload ?? []);
                setVehicleMakes(makesRes.data?.payload ?? []);
            } catch {}
        };
        void load();
        return () => {
            cancelled = true;
        };
    }, [company?.name]);

    const handleSearchResults = useCallback(
        (next: PricebookRule[], params: PricebookSearchParams) => {
            setRules(next);
            setLastParams(params);
        },
        [],
    );

    const refreshSearch = useCallback(async () => {
        if (!lastParams) return;
        setLoading(true);
        try {
            const res = await pricebookApi.searchPricebooks(lastParams);
            setRules(extractRules(res.data?.payload));
        } finally {
            setLoading(false);
        }
    }, [lastParams]);

    return (
        <Page title="Pricebook Management">
            {!isRepairer && company && Object.keys(company).length > 0 && (
                <Box sx={styles.warning(theme)}>
                    This feature is intended for repairer companies.
                </Box>
            )}
            <SearchRules
                catalog={catalog}
                vehicleMakes={vehicleMakes}
                onLoadingChange={setLoading}
                onSearchResults={handleSearchResults}
            />
            {loading && (
                <Box sx={styles.loadingRow}>
                    <CircularProgress />
                </Box>
            )}
            {!loading && rules !== null && rules.length === 0 && (
                <Typography sx={styles.emptyMessage(theme)}>
                    No rules found matching your search criteria.
                </Typography>
            )}
            {!loading && rules !== null && (
                <RulesTable
                    pricebook={pricebook}
                    rules={rules}
                    loading={false}
                    catalog={catalog}
                    vehicleMakes={vehicleMakes}
                    companyName={company?.name}
                    onRulesChanged={refreshSearch}
                />
            )}
        </Page>
    );
};

export default PricebooksPage;
